import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Globe } from "lucide-react";
import { SUPPORTED_LANGS } from "@/i18n";

/**
 * LanguageSwitcher — Dropdown zur Sprachwahl.
 *
 * WICHTIG: Auf `/admin/*`-Routen wird die Anzeige unterdrückt UND die Sprache
 * temporär auf `de` gezwungen (siehe `useAdminLangGuard`).
 */
export default function LanguageSwitcher({ compact = false }) {
  const { i18n, t } = useTranslation();
  const { pathname } = useLocation();

  // Admin-Routes: Sprachauswahl ausblenden (Admin bleibt deutsch)
  const isAdmin = pathname.startsWith("/admin");
  if (isAdmin) return null;

  const current = i18n.language?.split("-")[0] || "de";

  const change = (code) => {
    i18n.changeLanguage(code);
    // localStorage-Persistenz wird von i18next-browser-languagedetector automatisch gemacht
  };

  return (
    <div className="relative inline-block" data-testid="lang-switcher">
      <select
        value={current}
        onChange={(e) => change(e.target.value)}
        aria-label={t("lang_switcher_extra.aria")}
        data-testid="lang-switcher-select"
        className={`appearance-none bg-white border border-[#E5E5E5] px-2 pr-6 rounded text-xs text-[#0A0A0A] focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] cursor-pointer ${compact ? "h-7" : "h-8"}`}
      >
        {SUPPORTED_LANGS.map((l) => (
          <option key={l.code} value={l.code}>
            {compact ? l.code.toUpperCase() : `${l.flag} ${l.label}`}
          </option>
        ))}
      </select>
      <Globe size={12} className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-[#666]" />
    </div>
  );
}

/**
 * useAdminLangGuard — Hook, den `AdminDashboardPage` mountet.
 * Zwingt beim Betreten von /admin die i18n-Sprache auf `de` und stellt beim Verlassen
 * die vorherige Präferenz wieder her.
 */
export function useAdminLangGuard() {
  const { i18n } = useTranslation();
  useEffect(() => {
    const previous = i18n.language;
    if (previous !== "de") {
      i18n.changeLanguage("de");
    }
    return () => {
      // Beim Verlassen die vorherige User-Wahl (aus localStorage) restore
      const userPref = window.localStorage.getItem("stx_lang");
      if (userPref && userPref !== "de") {
        i18n.changeLanguage(userPref);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
